// Formats server-rendered <time> elements in the viewer's own zone.
// Rows render once for every viewer (and again for live pushes), so the
// server writes a UTC fallback and the browser localises it:
//   [data-relative-time]  "just now", "3h ago", then "Sep 4" past a week
//   [data-absolute-time]  "Sep 4, 3:00 PM"
//   [data-clock-time]     "3:00 PM", or "Wed 12:00 AM" when the viewer's
//                         date differs from the data-clock-day the
//                         server grouped it under
// The title always carries the full local timestamp. Any
// [data-local-zone] element reads the viewer's zone, such as "PDT".

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const relative = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
const sameYear = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
});
export const otherYear = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  year: "numeric",
});
const absolute = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});
const clock = new Intl.DateTimeFormat(undefined, {
  hour: "numeric",
  minute: "2-digit",
});
const weekday = new Intl.DateTimeFormat(undefined, { weekday: "short" });
// en-CA formats a date as YYYY-MM-DD, the shape data-clock-day carries.
const isoDay = new Intl.DateTimeFormat("en-CA", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const zoneName = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" });
const full = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

function relativeLabel(date, now) {
  const diff = date.getTime() - now;
  const ago = Math.abs(diff);
  if (ago < MINUTE) return "just now";
  if (ago < HOUR) return relative.format(Math.round(diff / MINUTE), "minute");
  if (ago < DAY) return relative.format(Math.round(diff / HOUR), "hour");
  if (ago < 7 * DAY) return relative.format(Math.round(diff / DAY), "day");

  const fmt =
    date.getFullYear() === new Date(now).getFullYear() ? sameYear : otherYear;
  return fmt.format(date);
}

// A slot the server grouped under the agent's day can fall on another
// day for a far-away viewer, so the weekday says which.
function clockLabel(el, date) {
  const time = clock.format(date);
  const day = el.getAttribute("data-clock-day");
  if (!day || isoDay.format(date) === day) return time;

  return `${weekday.format(date)} ${time}`;
}

function format(el, now) {
  const date = new Date(el.getAttribute("datetime"));
  if (Number.isNaN(date.getTime())) return;

  if (el.hasAttribute("data-relative-time")) {
    el.textContent = relativeLabel(date, now);
  } else if (el.hasAttribute("data-clock-time")) {
    el.textContent = clockLabel(el, date);
  } else {
    el.textContent = absolute.format(date);
  }
  el.title = full.format(date);
}

const SELECTOR =
  "time[data-relative-time], time[data-absolute-time], time[data-clock-time]";

function formatAll(root) {
  const now = Date.now();
  if (root.matches?.(SELECTOR)) format(root, now);
  root.querySelectorAll?.(SELECTOR).forEach((el) => format(el, now));

  const zone = zoneName
    .formatToParts(new Date(now))
    .find((part) => part.type === "timeZoneName")?.value;
  if (zone) {
    root.querySelectorAll?.("[data-local-zone]").forEach((el) => {
      el.textContent = zone;
    });
  }
}

let ticking = false;

// initRelativeTime formats every <time> under root. The minute refresh
// starts the first time a relative timestamp is on the page. Live
// timeline pushes land outside htmx, so streaming.js calls the global
// formatter on each pushed row.
export function initRelativeTime(root = document) {
  formatAll(root);
  window.formatRelativeTimes = formatAll;
  if (ticking || !document.querySelector("time[data-relative-time]")) return;
  ticking = true;

  // "3m ago" should not read "3m ago" an hour later.
  setInterval(() => {
    const now = Date.now();
    document
      .querySelectorAll("time[data-relative-time]")
      .forEach((el) => format(el, now));
  }, MINUTE);
}
